import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { FromDictionaryPipe, LanguageService } from '@teamfoster/sdk/dictionary-ngrx';
import { DynamicFormField, FormComponent } from '@teamfoster/dynamic-forms';
import { Subject } from 'rxjs';
import { Breadcrumb } from 'src/app/core/models/breadcrumb.model';
import {
  Customer,
  getCustomer,
  getCustomerLoading,
  getCustomerUpdated,
  getCustomerUpdating,
  getCustomerUpdatingErrors,
  UpdateCustomer,
} from 'src/app/shop';
import { VatNumberService } from '../../services/vat-number.service';
import { VatNumberValidator } from '../../validators';

@Component({
  selector: 'app-account-profile-edit',
  templateUrl: './account-profile-edit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AccountProfileEditComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('form') formEl?: FormComponent;

  private store = inject(Store);
  private lang = inject(LanguageService);
  private vatNumberService = inject(VatNumberService);
  private cd = inject(ChangeDetectorRef);
  private dict = new FromDictionaryPipe(this.lang);

  prefix = 'profiel-bewerken';

  formFields$: DynamicFormField[] = [];
  formFields: DynamicFormField[] = [];

  customerUpdating$ = this.store.selectSignal(getCustomerUpdating);
  customerUpdateSuccess$ = this.store.selectSignal(getCustomerUpdated);
  customerUpdateErrors$ = this.store.selectSignal(getCustomerUpdatingErrors);
  customerLoading$ = this.store.selectSignal(getCustomerLoading);

  customer$: any = this.store.selectSignal(getCustomer);

  breadcrumbs: Breadcrumb[] = [];
  private _unsubscribe = new Subject<void>();

  fieldsets: { name: string; title?: string; collapsed?: boolean }[] = [
    { name: 'profile', title: 'Profiel', collapsed: false },
    { name: 'vat', title: 'Factuuradres', collapsed: false },
    { name: 'other', title: 'Overig', collapsed: false },
  ];

  setForm$ = computed(() => {
    const customer = this.customer$();
    const loading = this.customerLoading$();
    return customer && !loading;
  });

  customerForm$ = effect(() => {
    const customer = this.customer$();
    // const loading = this.customerLoading$();
    const setForm = this.setForm$();

    // check is data is loaded
    if (!setForm) {
      this.formFields = [];
      return;
    }

    this.formFields = [
      {
        inputType: 'text',
        label: this.dict.transform(`formulier-veld-label-voornaam`),
        name: 'firstName',
        validators: [Validators.required],
        autocomplete: 'given-name',
        placeholder: this.dict.transform(`formulier-veld-placeholder-voornaam`, false),
        fieldType: 'input',
        order: 1,
        value: customer.firstName,
        cssClass: 'col-12 col-md-6',
        fieldset: 'profile',
      },
      {
        inputType: 'text',
        label: this.dict.transform(`formulier-veld-label-achternaam`),
        name: 'lastName',
        validators: [Validators.required],
        autocomplete: 'family-name',
        placeholder: this.dict.transform(`formulier-veld-placeholder-achternaam`, false),
        fieldType: 'input',
        order: 1,
        value: customer.lastName,
        cssClass: 'col-12 col-md-6',
        fieldset: 'profile',
      },
      {
        inputType: 'email',
        label: this.dict.transform(`formulier-veld-label-email`),
        name: 'email',
        validators: [Validators.email, Validators.required],
        autocomplete: 'email',
        placeholder: this.dict.transform(`formulier-veld-placeholder-email`, false),
        fieldType: 'input',
        order: 0,
        value: customer.email,
        fieldset: 'profile',
      },
      {
        inputType: 'text',
        label: this.dict.transform(`formulier-veld-label-telefoon`),
        name: 'phone',
        validators: [],
        fieldType: 'input',
        placeholder: this.dict.transform(`formulier-veld-placeholder-telefoon`, false),
        hint: this.dict.transform(`formulier-veld-hint-telefoon`, false),
        order: 1,
        value: customer.phone,
        fieldset: 'profile',
      },
      {
        fieldType: 'connected-address',
        checkboxLabel: this.dict.transform(`formulier-veld-verbonden-adres-label`),
        name: 'defaultAddress',
        validators: [],
        fieldset: 'vat',
        address: customer.defaultAddress ? customer.defaultAddress : null,
      },
      {
        inputType: 'text',
        label: this.dict.transform(`formulier-veld-label-vat-nummer`),
        name: 'vat',
        validators: [],
        disabled: !customer.defaultAddress,
        asyncValidators: [VatNumberValidator.createValidator(this.vatNumberService, this.cd)],
        placeholder: this.dict.transform(`formulier-veld-placeholder-vat-nummer`, false),
        fieldType: 'NIEUW',
        order: 0,
        value: customer.metafield?.value,
        fieldset: 'vat',
        customValidationMessages: {
          VatNumberInvalid: this.dict.transform(`formulier-veld-foutmelding-vat-nummer`),
        },
        showStates: true, // show loading & validated valid/invalid indicators in field
      },

      {
        fieldType: 'checkbox',
        checkboxLabel: this.dict.transform(`formulier-veld-marketing-label`),
        name: 'acceptsMarketing',
        validators: [],
        order: 1,
        value: customer.acceptsMarketing,
        fieldset: 'other',
      },
    ];
  });

  constructor() {} // private cd: ChangeDetectorRef // private vatNumberService: VatNumberService, // private store: Store, // private lang: LanguageService,

  ngOnInit(): void {
    this.breadcrumbs = [
      {
        label: this.dict.transform(`account-menu-titel`),
        routerLink: ['/account'],
      },
      {
        label: this.dict.transform(`account-profiel-bewerken-menu-titel`),
        routerLink: ['/account/bewerken'],
      },
    ];
  }

  ngAfterViewInit(): void {
    // this.formEl?.form.statusChanges.pipe(distinctUntilChanged()).subscribe(a => {
    //   this.cd.markForCheck();
    // });
  }

  ngOnDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }

  async handleSubmit(formValue: Customer) {
    if (!formValue.phone) {
      delete formValue.phone;
    }
    delete formValue.defaultAddress;
    this.store.dispatch(UpdateCustomer({ customer: formValue }));
  }

  getServerError(hasOtherErrors = false) {
    return this.formEl?.triedSubmit && !this.formEl?.form.valid && !hasOtherErrors;
  }
}
